import { motion } from 'framer-motion';
import last from 'lodash/last';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveLocalizedText } from '@/helpers/resolveLocalizedText';
import { TDR } from 'tdr-common';
import { SearchFilters, AvailabilityGuardContext } from '../routes/AvailabilityGuard';
import { Tab } from './shared/Tab';
import { useOutletContext } from 'react-router-dom';

interface UpdatedAvailabilityGuardContext extends AvailabilityGuardContext {
	searchFilters: SearchFilters;
	setSearchFilters: React.Dispatch<React.SetStateAction<SearchFilters>>;
}

export type ServiceSelectorProps = {
	searchFilters: SearchFilters;
	selectedServiceName: string;
	setSelectedServiceName: React.Dispatch<React.SetStateAction<string>>;
	servicesMap: TDR.ServiceTimeSlotMap;
	onChange: (newServiceName: string) => void;
};

const ServiceSelector = ({
	searchFilters,
	selectedServiceName,
	setSelectedServiceName,
	servicesMap,
	onChange
}: ServiceSelectorProps) => {
	const { t, i18n } = useTranslation();
	const context = useOutletContext<UpdatedAvailabilityGuardContext>();
	const setSearchFilters = context?.setSearchFilters;

	// If no selected time, select the last service that has availability
	useEffect(() => {
		if (servicesMap && !searchFilters.time) {
			const serviceOptions = Object.entries(servicesMap);
			const availableServices =
				serviceOptions
					.filter(([, { timeSlots }]) => Object.keys(timeSlots).length > 0)
					?.map(([name]) => name) || [];
			const defaultServiceName = last(availableServices);

			if (defaultServiceName && !serviceOptions.map(([name]) => name).includes(selectedServiceName)) {
				onChange(defaultServiceName);
			}
		}
	}, [selectedServiceName, servicesMap, onChange]);

	const handleChange = (serviceName: string) => {
		if (serviceName) {
			// Check if the new service supports the currently selected time
			const newService = servicesMap[serviceName];
			const timeSupported =
				searchFilters.time && newService?.timeSlots?.[searchFilters.time];

			// If the new service doesn't support the current time, reset the time
			if (!timeSupported && setSearchFilters) {
				setSearchFilters((prev) => ({ ...prev, time: '' }));
			}

			setSelectedServiceName(serviceName);
			onChange(serviceName);
		}
	};

	const serviceOptions = servicesMap
		? Object.entries(servicesMap).map(([name]) => ({
			name,
			displayName: resolveLocalizedText(name, i18n.language)
		}))
		: [];

	return (
		<motion.div
			aria-label={t('aria.time_of_day_selector_menu')}
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			exit={{ opacity: 0 }}
		>
			<Tab
				value={selectedServiceName}
				onValueChange={handleChange}
				options={serviceOptions}
			/>
		</motion.div>
	);
};

export default ServiceSelector;